/**
 * Admin — Ranking por tienda / entidad.
 * Muestra promedio de exámenes, insignias oro/plata y densidad por vendedor
 * para motivar competencia sana entre tiendas.
 */
import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Trophy, Medal, Award, Users, Store as StoreIcon,
  RefreshCw, TrendingUp, Loader2, BarChart3, Star,
} from "lucide-react";

import { api } from "@/api/client";
import { useLang } from "@/context/LangContext";

const GOLD = "#FFD100";
const SILVER = "#C0C0C0";
const NAVY = "#0d1a3f";
const GREEN = "#2E7D32";
const RED = "#E3000F";

const StatCard = ({ Icon, label, value, sub, color = "#005BBB" }) => (
  <div className="brutal p-4 bg-white flex items-center gap-3">
    <div
      className="w-12 h-12 rounded-2xl border-2 border-black flex items-center justify-center shrink-0"
      style={{ background: color }}
    >
      <Icon className="w-6 h-6 text-white" />
    </div>
    <div className="min-w-0">
      <div className="text-[10px] font-display font-bold uppercase tracking-widest text-neutral-600 truncate">
        {label}
      </div>
      <div className="font-display font-black text-2xl leading-none mt-1">{value}</div>
      {sub && (
        <div className="text-xs font-body text-neutral-500 mt-0.5">{sub}</div>
      )}
    </div>
  </div>
);

const podiumBg = (rank) => {
  if (rank === 0) return "bg-canales-yellow";
  if (rank === 1) return "bg-neutral-200";
  if (rank === 2) return "bg-orange-200";
  return "bg-white";
};

const scoreColor = (v) => (v >= 80 ? GREEN : v >= 60 ? "#B26A00" : RED);

export default function TeamRanking() {
  const { lang } = useLang();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [groupBy, setGroupBy] = useState("location"); // 'location' | 'entity'
  const [sortBy, setSortBy] = useState("combined_score"); // combined_score, avg_percent, gold_badges, silver_badges, attempts

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/team-ranking?group_by=${groupBy}`);
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [groupBy]);

  const rows = useMemo(() => {
    if (!data?.rows) return [];
    const arr = [...data.rows];
    arr.sort((a, b) => (b[sortBy] || 0) - (a[sortBy] || 0));
    return arr;
  }, [data, sortBy]);

  const top3 = rows.slice(0, 3);
  const rest = rows.slice(3);

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
      data-testid="admin-team-ranking-page"
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display font-black text-4xl flex items-center gap-3">
            <Trophy className="w-8 h-8 text-canales-red" />
            {lang === "es" ? "Ranking por Equipo" : "Team Ranking"}
          </h1>
          <p className="font-body text-neutral-700 mt-1">
            {lang === "es"
              ? "Competencia sana entre tiendas: promedio, insignias y densidad por vendedor."
              : "Healthy competition between stores: average, badges and per-seller density."}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap items-end">
          <label className="flex flex-col text-xs font-display font-bold uppercase tracking-widest text-neutral-600">
            <span>{lang === "es" ? "Agrupar por" : "Group by"}</span>
            <select
              value={groupBy}
              onChange={(e) => setGroupBy(e.target.value)}
              data-testid="team-ranking-group-by"
              className="brutal-sm px-3 py-2 bg-white font-body text-sm mt-1"
            >
              <option value="location">
                {lang === "es" ? "Tienda / Ubicación" : "Store / Location"}
              </option>
              <option value="entity">
                {lang === "es" ? "Entidad / Franquicia" : "Entity / Franchise"}
              </option>
            </select>
          </label>
          <label className="flex flex-col text-xs font-display font-bold uppercase tracking-widest text-neutral-600">
            <span>{lang === "es" ? "Ordenar por" : "Sort by"}</span>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              data-testid="team-ranking-sort-by"
              className="brutal-sm px-3 py-2 bg-white font-body text-sm mt-1"
            >
              <option value="combined_score">
                {lang === "es" ? "Score combinado" : "Combined score"}
              </option>
              <option value="avg_percent">
                {lang === "es" ? "Promedio %" : "Average %"}
              </option>
              <option value="gold_badges">
                {lang === "es" ? "Insignias Oro" : "Gold badges"}
              </option>
              <option value="silver_badges">
                {lang === "es" ? "Insignias Plata" : "Silver badges"}
              </option>
              <option value="attempts">
                {lang === "es" ? "Intentos" : "Attempts"}
              </option>
              <option value="pass_rate">
                {lang === "es" ? "% Aprobación" : "Pass rate"}
              </option>
            </select>
          </label>
          <button
            onClick={load}
            disabled={loading}
            data-testid="team-ranking-refresh"
            className="brutal-btn bg-white px-3 py-2 text-sm inline-flex items-center gap-1"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            {lang === "es" ? "Refrescar" : "Refresh"}
          </button>
        </div>
      </div>

      {loading && !data ? (
        <div className="p-10 text-center text-neutral-500">
          <Loader2 className="w-6 h-6 animate-spin inline-block" />
        </div>
      ) : !data ? (
        <div className="p-10 text-center text-neutral-500 font-body">
          {lang === "es" ? "Sin datos" : "No data"}
        </div>
      ) : (
        <>
          {/* Totales */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="team-ranking-totals">
            <StatCard
              Icon={StoreIcon}
              label={lang === "es" ? "Tiendas" : "Stores"}
              value={data.totals.stores}
              sub={`${data.totals.sellers} ${lang === "es" ? "vendedores" : "sellers"} · ${data.totals.managers} managers`}
              color={NAVY}
            />
            <StatCard
              Icon={BarChart3}
              label={lang === "es" ? "Intentos totales" : "Total attempts"}
              value={data.totals.attempts}
              sub={`${lang === "es" ? "Promedio global" : "Global average"}: ${data.totals.avg_percent}%`}
              color="#005BBB"
            />
            <StatCard
              Icon={Award}
              label={lang === "es" ? "Insignias Oro" : "Gold badges"}
              value={data.totals.gold_badges}
              sub={lang === "es" ? "≥ 90% de puntuación" : "≥ 90% score"}
              color={GOLD}
            />
            <StatCard
              Icon={Medal}
              label={lang === "es" ? "Insignias Plata" : "Silver badges"}
              value={data.totals.silver_badges}
              sub={lang === "es" ? "70–89% de puntuación" : "70–89% score"}
              color={SILVER}
            />
          </div>

          {/* Podio */}
          {top3.length > 0 && (
            <section className="grid md:grid-cols-3 gap-3" data-testid="team-ranking-podium">
              {top3.map((r, i) => (
                <div
                  key={r.key}
                  className={`brutal p-5 flex flex-col gap-2 ${podiumBg(i)}`}
                  data-testid={`team-ranking-podium-${i + 1}`}
                >
                  <div className="flex items-center gap-2">
                    {i === 0 ? (
                      <Trophy className="w-6 h-6 text-black" />
                    ) : i === 1 ? (
                      <Medal className="w-6 h-6 text-black" />
                    ) : (
                      <Star className="w-6 h-6 text-black" />
                    )}
                    <span className="font-display font-black text-lg">
                      #{i + 1}
                    </span>
                  </div>
                  <div className="font-display font-black text-xl leading-tight truncate">
                    {r.key}
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div className="brutal-sm bg-white/70 p-2">
                      <div className="text-[10px] uppercase tracking-widest font-display font-bold text-neutral-600">
                        {lang === "es" ? "Promedio" : "Average"}
                      </div>
                      <div
                        className="font-display font-black text-2xl leading-none"
                        style={{ color: scoreColor(r.avg_percent) }}
                      >
                        {r.avg_percent}%
                      </div>
                    </div>
                    <div className="brutal-sm bg-white/70 p-2">
                      <div className="text-[10px] uppercase tracking-widest font-display font-bold text-neutral-600">
                        {lang === "es" ? "Aprobación" : "Pass rate"}
                      </div>
                      <div
                        className="font-display font-black text-2xl leading-none"
                        style={{ color: scoreColor(r.pass_rate) }}
                      >
                        {r.pass_rate}%
                      </div>
                    </div>
                    <div className="brutal-sm bg-white/70 p-2 inline-flex items-center gap-1">
                      <Award className="w-4 h-4" style={{ color: GOLD }} />
                      <div>
                        <div className="text-[10px] uppercase tracking-widest font-display font-bold text-neutral-600">
                          {lang === "es" ? "Oro" : "Gold"}
                        </div>
                        <div className="font-display font-black text-lg leading-none">
                          {r.gold_badges}
                        </div>
                      </div>
                    </div>
                    <div className="brutal-sm bg-white/70 p-2 inline-flex items-center gap-1">
                      <Medal className="w-4 h-4" style={{ color: "#7d7d7d" }} />
                      <div>
                        <div className="text-[10px] uppercase tracking-widest font-display font-bold text-neutral-600">
                          {lang === "es" ? "Plata" : "Silver"}
                        </div>
                        <div className="font-display font-black text-lg leading-none">
                          {r.silver_badges}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="text-xs font-body text-neutral-700 mt-1">
                    {r.sellers} {lang === "es" ? "vendedores" : "sellers"} ·{" "}
                    {r.managers} managers · {r.attempts}{" "}
                    {lang === "es" ? "intentos" : "attempts"}
                  </div>
                </div>
              ))}
            </section>
          )}

          {/* Tabla completa */}
          <section className="brutal bg-white overflow-x-auto" data-testid="team-ranking-table">
            <table className="w-full text-sm">
              <thead className="bg-canales-cream border-b-2 border-black text-xs uppercase font-display font-black tracking-widest">
                <tr>
                  <th className="text-left px-4 py-3">#</th>
                  <th className="text-left px-4 py-3">
                    {lang === "es" ? "Tienda / Entidad" : "Store / Entity"}
                  </th>
                  <th className="text-right px-4 py-3">
                    {lang === "es" ? "Vendedores" : "Sellers"}
                  </th>
                  <th className="text-right px-4 py-3">
                    {lang === "es" ? "Intentos" : "Attempts"}
                  </th>
                  <th className="text-right px-4 py-3">
                    {lang === "es" ? "Promedio" : "Average"}
                  </th>
                  <th className="text-right px-4 py-3">
                    {lang === "es" ? "% Aprob." : "Pass %"}
                  </th>
                  <th className="text-right px-4 py-3" title={lang === "es" ? "Puntuación ≥ 90%" : "Score ≥ 90%"}>
                    <Award className="w-3.5 h-3.5 inline-block mr-1" style={{ color: GOLD }} />
                    {lang === "es" ? "Oro" : "Gold"}
                  </th>
                  <th className="text-right px-4 py-3" title={lang === "es" ? "Puntuación 70–89%" : "Score 70–89%"}>
                    <Medal className="w-3.5 h-3.5 inline-block mr-1" style={{ color: "#7d7d7d" }} />
                    {lang === "es" ? "Plata" : "Silver"}
                  </th>
                  <th className="text-right px-4 py-3" title={lang === "es" ? "Score combinado (promedio + densidad de insignias)" : "Combined score"}>
                    <TrendingUp className="w-3.5 h-3.5 inline-block mr-1" />
                    Score
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={9} className="text-center text-neutral-500 py-6 font-body">
                      {lang === "es" ? "Sin equipos" : "No teams"}
                    </td>
                  </tr>
                )}
                {rows.map((r, i) => (
                  <tr
                    key={r.key}
                    data-testid={`team-ranking-row-${i}`}
                    className={`border-b border-neutral-200 ${i < 3 ? "bg-canales-cream/40" : ""}`}
                  >
                    <td className="px-4 py-3 font-display font-black">
                      #{i + 1}
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-display font-bold">{r.key}</div>
                      <div className="text-xs font-body text-neutral-500">
                        {r.managers} managers ·{" "}
                        {r.unique_test_takers} {lang === "es" ? "activos" : "active"}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right font-mono">
                      {r.sellers}
                    </td>
                    <td className="px-4 py-3 text-right font-mono">
                      {r.attempts}
                    </td>
                    <td
                      className="px-4 py-3 text-right font-display font-black"
                      style={{ color: scoreColor(r.avg_percent) }}
                    >
                      {r.attempts ? `${r.avg_percent}%` : "—"}
                    </td>
                    <td
                      className="px-4 py-3 text-right font-display font-bold"
                      style={{ color: r.attempts ? scoreColor(r.pass_rate) : "#a1a1aa" }}
                    >
                      {r.attempts ? `${r.pass_rate}%` : "—"}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span
                        className="inline-flex items-center gap-1 brutal-sm px-2 py-0.5 font-display font-black text-xs"
                        style={{ background: GOLD }}
                      >
                        <Award className="w-3 h-3" />
                        {r.gold_badges}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className="inline-flex items-center gap-1 brutal-sm px-2 py-0.5 font-display font-black text-xs bg-neutral-200">
                        <Medal className="w-3 h-3" />
                        {r.silver_badges}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span
                        className="inline-flex items-center gap-1 brutal-sm px-2 py-0.5 font-display font-black text-xs bg-canales-blue text-white"
                      >
                        {r.combined_score}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>

          <div className="text-xs font-body text-neutral-500 flex flex-wrap gap-3">
            <span className="inline-flex items-center gap-1">
              <Award className="w-3 h-3" style={{ color: GOLD }} />{" "}
              {lang === "es"
                ? "Oro = puntuación ≥ 90% en un examen"
                : "Gold = ≥ 90% on an exam"}
            </span>
            <span className="inline-flex items-center gap-1">
              <Medal className="w-3 h-3" style={{ color: "#7d7d7d" }} />{" "}
              {lang === "es"
                ? "Plata = puntuación 70–89% en un examen"
                : "Silver = 70–89% on an exam"}
            </span>
            <span>
              {lang === "es"
                ? "Score combinado = 60% del promedio + 40% de la densidad de insignias por vendedor."
                : "Combined = 60% average + 40% badge density per seller."}
            </span>
          </div>
        </>
      )}
    </motion.div>
  );
}
